<template>
  <div class="container">
    <header class="page-header">
      <h1 class="page-title">Assinaturas</h1>
      <BaseButton
        class="page-header__profile-btn"
        variant="ghost"
        size="sm"
        @click="goToProfile"
      >
        <v-icon name="md-accountcircle" /> Meus dados
      </BaseButton>
    </header>

    <div v-if="loading" class="empty-state">Carregando templates...</div>
    <div v-else-if="tplError" class="empty-state">{{ tplError }}</div>
    <div v-else-if="templates.length === 0" class="empty-state">Nenhum template de assinatura encontrado.</div>

    <div v-else class="templates-section">
      <SignaturePreview
        v-bind:key="currentTemplate.id"
        v-bind:template="currentTemplate"
        v-bind:user-data="userData"
        v-bind:is-applying="applyingId === currentTemplate.id"
        v-bind:has-prev="templates.length > 1"
        v-bind:has-next="templates.length > 1"
        v-bind:index="currentIndex + 1"
        v-bind:total="templates.length"
        v-bind:transition-name="transitionName"
        v-bind:preview-theme-light="previewThemeLight"
        v-on:apply="onApplySignature"
        v-on:prev="prevTemplate"
        v-on:next="nextTemplate"
        v-on:toggle-theme="previewThemeLight = !previewThemeLight"
      />
    </div>

    <div class="settings-card">
      <SignatureSettings
      :templates="templates"
      :new-mail="savedNewMail"
      :reply="savedReply"
      :forward="savedForward"
      :mandatory-template-id="mandatoryTemplateId"
      @save="onSaveSettings"
      @preview-signature="onPreviewSignature"
    />
    </div>

    <div class="logout-section">
      <BaseButton variant="ghost" block @click="handleLogout">
        <v-icon name="md-logout" /> Sair da conta
      </BaseButton>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, onUnmounted } from "vue";
import { useRouter } from "vue-router";
import { useAuth } from "@/features/auth/composables/useAuth";
import { useTemplates } from "@/features/signature/composables/useTemplates";
import { useSignature } from "@/features/signature/composables/useSignature";
import { syncUserDataFromGraph } from "@/features/profile/composables/useGraph";
import { useLogout } from "@/shared/composables/useLogout";
import SignaturePreview from "@/features/signature/components/SignaturePreview.vue";
import SignatureSettings from "@/features/signature/components/SignatureSettings.vue";
import BaseButton from "@/shared/components/BaseButton.vue";
import {
  reconcileTemplateSlots,
  getTemplateSlot,
  indexOfTemplateKey,
  templateKey,
  findMandatory,
  TEMPLATES_REFRESH_MS,
} from "@/features/signature/services/template-cache";
import { flushRoaming } from "@/shared/services/storage";
import { userDataCache } from "@/shared/services/user-data-cache";
import { useToast } from "@/shared/composables/useToast";
import type { UserTemplateData, SignatureTemplate } from "@/shared/types";

const router = useRouter();
const toast = useToast();
const { init, isLoggedIn, getMsalInstance } = useAuth();
const { doLogout } = useLogout();
const { templates, isLoading: loading, error: tplError, loadTemplates, loadUserInfo } = useTemplates();
const { applySignature, saveSettings } = useSignature();

const userData = ref<UserTemplateData>({});
const applyingId = ref<number | null>(null);
const currentIndex = ref(0);
const previewThemeLight = ref(true);
const transitionName = ref<"slide-left" | "slide-right">("slide-left");
const savedNewMail = ref("none");
const savedReply = ref("none");
const savedForward = ref("none");
const warnedStaleData = ref(false);

const currentTemplate = computed(() =>
  templates.value[currentIndex.value] ?? templates.value[0]
);

/** Id do template imposto pela organização, ou `null` quando a escolha é livre. */
const mandatoryTemplateId = computed<number | null>(
  () => findMandatory(templates.value as SignatureTemplate[])?.id ?? null
);

function prevTemplate(): void {
  if (templates.value.length === 0) return;
  transitionName.value = "slide-right";
  currentIndex.value =
    (currentIndex.value - 1 + templates.value.length) % templates.value.length;
}

function nextTemplate(): void {
  if (templates.value.length === 0) return;
  transitionName.value = "slide-left";
  currentIndex.value =
    (currentIndex.value + 1) % templates.value.length;
}

let pollTimer: ReturnType<typeof setInterval> | null = null;

/**
 * Rebusca as assinaturas do usuário a cada 10 minutos enquanto o painel estiver
 * aberto, para que atribuições feitas na plataforma apareçam sem precisar
 * reabrir o suplemento.
 */
function startTemplatePolling(): void {
  stopTemplatePolling();
  pollTimer = setInterval(async () => {
    const info = loadUserInfo();
    const identity = userDataCache.identityEmail();
    if (!identity) return;

    const onScreenId = currentTemplate.value?.id;

    await loadTemplates(identity, info?.name || info?.display_name || "");
    if (info) userData.value = info;

    await applySlotReconciliation();
    syncPreviewToSelection(onScreenId);
  }, TEMPLATES_REFRESH_MS);
}

function stopTemplatePolling(): void {
  if (pollTimer) {
    clearInterval(pollTimer);
    pollTimer = null;
  }
}

onUnmounted(stopTemplatePolling);

onMounted(async () => {
  await Office.onReady();
  await init();

  if (!isLoggedIn.value) { router.push("/login"); return; }

  const info = loadUserInfo();
  if (info) {
    userData.value = info;
    await loadTemplates(userDataCache.identityEmail(), info.name || info.display_name || "");
    await applySlotReconciliation();
  } else {
    router.push("/profile");
    return;
  }

  loadRoamingSettings();
  syncPreviewToSelection();

  startTemplatePolling();

  // Atualiza os dados do Graph em segundo plano, sem bloquear a renderização
  // inicial: se falhar (offline, etc.), a assinatura segue com o cache atual.
  refreshUserDataFromGraphInBackground();
});

async function refreshUserDataFromGraphInBackground(): Promise<void> {
  const { success, authExpired } = await syncUserDataFromGraph(getMsalInstance());

  if (authExpired) {
    if (!loadUserInfo()) {
      router.push("/login?reason=expired");
      return;
    }
    if (!warnedStaleData.value) {
      warnedStaleData.value = true;
      toast.info("Não foi possível atualizar seus dados do Microsoft 365. Exibindo os dados salvos.");
    }
    return;
  }

  if (success) {
    userData.value = loadUserInfo() || userData.value;
  }
}

/**
 * Alinha os slots com a lista viva de templates e persiste se algo mudou.
 */
async function applySlotReconciliation(): Promise<void> {
  if (tplError.value) return;
  if (reconcileTemplateSlots(templates.value as SignatureTemplate[])) {
    await flushRoaming();
  }
}

function loadRoamingSettings(): void {
  const nm = getTemplateSlot("new_mail");
  const rp = getTemplateSlot("reply");
  const fw = getTemplateSlot("forward");
  if (nm) savedNewMail.value = nm;
  if (rp) savedReply.value = rp;
  if (fw) savedForward.value = fw;
}

/**
 * Posiciona o carrossel na assinatura configurada.
 * @param preferredId - Id do template a manter em tela, se ainda existir.
 */
function syncPreviewToSelection(preferredId?: number): void {
  const byPreferred = preferredId != null
    ? indexOfTemplateKey(templates.value as SignatureTemplate[], templateKey(preferredId))
    : -1;
  const bySlot = indexOfTemplateKey(templates.value as SignatureTemplate[], savedNewMail.value);
  currentIndex.value = Math.max(0, byPreferred >= 0 ? byPreferred : bySlot);
}

function onApplySignature(templateId: number): void {
  const tpl = templates.value.find((t: any) => t.id === templateId);
  if (tpl) {
    applyingId.value = templateId;
    applySignature(tpl, userData.value);
    setTimeout(() => { applyingId.value = null; }, 500);
  }
}

function onSaveSettings(newMail: string, reply: string, forward: string): void {
  saveSettings(newMail, reply, forward, true);
}

function onPreviewSignature(templateId: number): void {
  const idx = templates.value.findIndex((t: SignatureTemplate) => t.id === templateId);
  if (idx >= 0) {
    currentIndex.value = idx;
  }
}

function goToProfile(): void { router.push("/profile"); }

async function handleLogout(): Promise<void> {
  try {
    await doLogout();
    toast.success("Você saiu da conta.");
  } catch (err: any) {
    toast.error("Erro ao sair da conta: " + (err?.message || "desconhecido"));
  }
}
</script>

<style scoped>
.container {
  width: 100%;
  max-width: 100%;
  display: flex;
  flex-direction: column;
  gap: var(--spacing-md);
  overflow-x: hidden;
}

.page-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding-bottom: var(--spacing-sm);
}

.page-title {
  margin: 0;
  font-size: var(--font-size-xl);
  font-weight: 600;
  color: var(--colorText);
}

.page-header__profile-btn {
  border: 1px solid var(--colorBorder) !important;
  border-radius: 20px !important;
  padding: var(--spacing-xs) var(--spacing-md) !important;
  transition: border-color var(--transition), background var(--transition), box-shadow var(--transition);

  &:hover {
    border-color: var(--colorPrimary) !important;
    background: var(--colorBackgroundSubtle) !important;
    box-shadow: 0 0 0 1px var(--colorBorderFocus);
  }
}

.empty-state {
  text-align: center;
  color: var(--colorTextSecondary);
  padding: var(--spacing-xl);
  font-size: var(--font-size-md);
  background: var(--colorBackground);
  border-radius: var(--radius-md);
  box-shadow: var(--shadow2);
}

.templates-section {
  display: flex;
  flex-direction: column;
  gap: var(--spacing-sm);
  position: relative;
}

.settings-card {
  background: var(--colorBackground);
  border-radius: var(--radius-md);
  box-shadow: var(--shadow2);
  padding: var(--spacing-lg);
}

.logout-section {
  padding-top: var(--spacing-md);
  border-top: 1px solid var(--colorBorderSubtle);
}
</style>
